import cn from 'classnames'

import { GetStaticPaths, GetStaticProps } from 'next'

import { getExchangePairs, getExchangePairByUrl } from 'store/fetchers/exchange-pairs/services'

import { useHeader } from 'utils/use-header'

import { IExchangePair } from 'store/fetchers/exchange-pairs/interfaces/exchange-pair-strapi.interface'

import { slides } from 'pages/home/components/swiper-component/assets/slides'

import ExchangeCrypto from 'pages/exchange-pair/exchange-crypto'
import HowToExchange from 'pages/exchange-pair/how-to-exchange'
import ExchangeCommunity from 'pages/exchange-pair/exchange-community'
import ExchangeFaq from 'pages/exchange-pair/exchange-faq'
import { mainQuestionsExchangePair } from 'pages/exchange-pair/index.p'
import { ExchangePairs } from 'pages/exchange-pair/exchange-pairs'
import ExchangePriceChart from 'pages/exchange-pair/exchange-price-chart'
import ExchangeGetStarted from 'pages/exchange-pair/get-started'
import ExchangeBuy from 'pages/exchange-pair/exchange-buy'

import styles from 'pages/exchange-pair/styles.module.scss'
import { useTranslation } from 'utils/use-translation'

export default function ExchangePair({ exchangePair }: { exchangePair: IExchangePair }) {
  const { t, replacePlaceholders, locale } = useTranslation()

  const fromCurrency = {
    ticker: exchangePair?.fromTicker || 'BTC',
    network: exchangePair?.fromNetwork || 'btc',
    name: exchangePair?.fromName || 'Bitcoin',
  }

  const toCurrency = {
    ticker: exchangePair?.toTicker || 'XMR',
    network: exchangePair?.toNetwork || 'xmr',
    name: exchangePair?.toName || 'Monero',
  }

  const fromCurrencyGraph = {
    ticker: exchangePair?.toTickerGraph || exchangePair?.fromTicker,
    network: exchangePair?.toNetworkGraph || exchangePair?.fromNetwork,
    name: exchangePair?.toNameGraph || exchangePair?.fromName,
  }

  const toCurrencyGraph = {
    ticker: exchangePair?.fromTickerGraph || exchangePair?.toTicker,
    network: exchangePair?.fromNetworkGraph || exchangePair?.toNetwork,
    name: exchangePair?.fromNameGraph || exchangePair?.toName,
  }
  const defaultFromAmount = exchangePair?.defaultFromAmount

  const questions = exchangePair?.questions?.questions || mainQuestionsExchangePair

  const placeholders = {
    nameFrom: `${fromCurrency.name} (${fromCurrency.ticker})`,
    nameTo: `${toCurrency.name} (${toCurrency.ticker})`
  }

  const head = useHeader(
    replacePlaceholders(t('title_exchange'), placeholders),
    replacePlaceholders(t('desc_exchange'), placeholders),
  )

  const defaultQuestions = [
    {
      title: t('what_is_exchange'),
      answer: t('what_is_exchange_answer'),
    },
    {
      title: t('how_trade'),
      answer: t('how_trade_answer'),
    },
    {
      title: t('what_affects'),
      answer: t('what_affects_answer'),
    },
    {
      title: t('how_exchange'),
      answer: t('how_exchange_answer'),
    },
  ]

  return (
    <>
      {exchangePair && (
        <div className={styles.exchange}>
          {head}
          <ExchangeCrypto fromCurrency={fromCurrency} toCurrency={toCurrency} defaultFromAmount={defaultFromAmount} />
          <section className={styles.section}>
            <div className={cn(styles.section__header, styles.margin)}>
              <h2 className={cn(styles.section__title)}>
                {t('how_swap')} {fromCurrency.ticker.toUpperCase()} {t('to')} {toCurrency.ticker.toUpperCase()}
              </h2>
            </div>
            <HowToExchange />
          </section>

          {!exchangePair?.isGraphDisabled && (
            <section className={styles.section}>
              <div className={cn(styles.section__header)}>
                <h2 className={cn(styles.section__title)}>
                  {toCurrencyGraph.name} {t('to')} {fromCurrencyGraph.name} {t('live_pie_chart')}
                </h2>
              </div>
              <ExchangePriceChart fromCurrency={fromCurrencyGraph} toCurrency={toCurrencyGraph} />
            </section>
          )}

          <section className={styles.section}>
            <div className={cn(styles.section__header)}>
              <h2 className={cn(styles.section__title)}>{t('get_started_with')} {fromCurrency.name}</h2>
            </div>
            <ExchangeGetStarted fromCurrency={fromCurrency} toCurrency={toCurrency} />
          </section>

          <section className={styles.section}>
            <div className={cn(styles.section__header)}>
              <h2 className={cn(styles.section__title)}>{t('other_options_swap')} {toCurrency.name}</h2>
            </div>
            <ExchangePairs toCurrency={toCurrency} />
          </section>

          <section className={styles.section}>
            <div className={cn(styles.section__header, styles['section__header_without-margin'])}>
              <h2 className={cn(styles.section__title)}>{t('crypto_community_trusts_us')}</h2>
            </div>
            <ExchangeCommunity slides={slides} />
          </section>

          <section className={styles.section}>
            <div className={cn(styles.section__header, styles['section__header_without-margin'])}>
              <h2 className={cn(styles.section__title)}>{t('buy')} {fromCurrency.name} {t('with_top_crypto')}</h2>
            </div>
            <ExchangeBuy fromCurrency={fromCurrency} />
          </section>

          <section className={styles.section}>
            <div className={styles.section__header}>
              <h2 className={cn(styles.section__title, styles.section__title_left)}>{t('faq')}</h2>
            </div>
            <ExchangeFaq questions={locale !== 'en' ? defaultQuestions : questions} />
          </section>
        </div>
      )}
    </>
  )
}

type Params = {
  pair: string
}

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const pair = Array.isArray(params?.pair) ? params.pair[0] : params?.pair
  if (!pair) {
    return {
      notFound: true,
    }
  }
  const res = await getExchangePairByUrl(pair)
  if (!res || !res.data || !res.data.attributes) {
    return {
      notFound: true,
    }
  }

  return { props: { exchangePair: res.data.attributes } }
}

export const getStaticPaths: GetStaticPaths<Params> = async ({ locales }) => {
  const res = await getExchangePairs()

  if (!res || !res.data) {
    return { paths: [], fallback: true }
  }

  const exchangePairs = res.data

  const paths = exchangePairs.flatMap((pair) =>
    locales.map((locale) => ({
      params: { pair: pair.attributes.pageUrl },
      locale,
    }))
  )

  return { paths, fallback: true }
}
